import { branchApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetBranchParamsI,
  BranchStkI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getBranches = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetBranchParamsI): Promise<ServiceResponseI<DataI<BranchStkI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());
    if (status !== undefined) params.append("status", String(status));

    const { data } = await branchApi.get<ServiceResponseI<DataI<BranchStkI>>>(
      "",
      {
        params,
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllBranches = async (
  clientId?: number | null,
): Promise<ServiceResponseI<BranchStkI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await branchApi.get<ServiceResponseI<BranchStkI[]>>(
      "/all-no-pagination",
      {
        params: { clientId: clientId ?? null },
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const branchById = async (
  id: string,
): Promise<ServiceResponseI<BranchStkI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await branchApi.get<ServiceResponseI<BranchStkI>>(
      `/${id}`,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllBranchsByToken = async (): Promise<
  ServiceResponseI<BranchStkI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await branchApi.get<ServiceResponseI<BranchStkI[]>>(
      "/all-no-pagination-by-token",
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const branchPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await branchApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const branchPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await branchApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const branchDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await branchApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useBranches = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetBranchParamsI) => {
  const branchesQuery = useQuery(
    ["branches", { pagination, sorting, globalFilter, clientId, status }],
    () => getBranches({ pagination, sorting, globalFilter, clientId, status }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { branchesQuery };
};
